/**
 * The editor's frame, like Unreal Engine's: the menu bar (File, Edit, View, Window, Tools,
 * Help), the toolbar under it, the windows, and the status bar. This service holds what the menu
 * bar and the toolbar show; nobody owns them: subsystems contribute sections and buttons
 * (`menus` and `toolbar` in `app/subsystems.ts`, as data next to each service), and so can
 * anything that comes and goes (plugins), with {@link Shell.addMenus} and
 * {@link Shell.addToolbar}. The shell draws them (`app/MenuBar.tsx`, `app/Toolbar.tsx`).
 */
import { createSignal, type Accessor } from "solid-js";
import { MENU_BAR, menuOf, toolbarGroups, type MenuBarId, type MenuEntry, type MenuSection, type ToolbarButton } from "../shared/menus";

/** The shell service. */
export interface Shell {
  /** The menu bar's menus, in order. */
  menus: () => readonly { id: MenuBarId; label: string }[];
  /** The lines of a menu bar's menu, built now. */
  menu(id: MenuBarId): MenuEntry[];
  /** The toolbar's buttons, in groups (reactive). */
  toolbar: Accessor<ToolbarButton[][]>;
  /** Adds menu sections; returns a function taking them out again. */
  addMenus(sections: readonly MenuSection[]): () => void;
  /** Adds toolbar buttons; returns a function taking them out again. */
  addToolbar(buttons: readonly ToolbarButton[]): () => void;
}

/** Creates the shell service, with nothing in its menus yet. */
export function createShell(): Shell {
  const [sections, setSections] = createSignal<readonly MenuSection[]>([]);
  const [buttons, setButtons] = createSignal<readonly ToolbarButton[]>([]);
  return {
    menus: () => MENU_BAR,
    menu: (id) => menuOf(sections(), id),
    toolbar: () => toolbarGroups(buttons()),
    addMenus(added) {
      setSections((all) => [...all, ...added]);
      return () => setSections((all) => all.filter((s) => !added.includes(s)));
    },
    addToolbar(added) {
      setButtons((all) => [...all, ...added]);
      return () => setButtons((all) => all.filter((b) => !added.includes(b)));
    },
  };
}
